import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive'

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  fullWidth?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
  children?: ReactNode
}

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-green-700 hover:bg-green-800 disabled:bg-ink-200 disabled:text-ink-400 text-white border border-green-800',
  secondary:
    'bg-cream-card hover:bg-ink-100/60 disabled:opacity-60 text-ink-900 border border-ink-200',
  ghost:
    'bg-transparent hover:bg-ink-100/60 disabled:opacity-50 text-ink-700 border border-transparent',
  destructive:
    'bg-flag hover:bg-flag-dark disabled:opacity-60 text-white border border-flag-dark',
}

const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      variant = 'primary',
      fullWidth,
      iconLeft,
      iconRight,
      children,
      className = '',
      type = 'button',
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      // eslint-disable-next-line react/button-has-type
      type={type}
      className={`
        inline-flex items-center justify-center gap-1.5
        min-h-[44px] px-4 rounded-lg
        font-semibold text-sm
        transition-colors
        focus:outline-none focus-visible:ring-2 focus-visible:ring-green-700/30
        ${VARIANT_CLASSES[variant]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...rest}
    >
      {iconLeft}
      {children}
      {iconRight}
    </button>
  )
)

Button.displayName = 'Button'

export default Button
